'use client'
import { useCarousel } from '@/hooks/useCarousel'

import type { FeatureCarouselFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import { StructuredText } from 'react-datocms'
import { TextWithHighlight } from '../TextWithHighlight'
import Eyebrow from '../ui/Eyebrow'
import IconButton from '../ui/IconButton'
import CircleGraphic from '../ui/CircleGraphic'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import CtaDefault from '../ui/cta/CtaDefault'

export default function FeatureCarouselSection({
	theme,
	tag,
	title,
	description,
	features,
	cta,
}: Readonly<FeatureCarouselFragment>) {
	const { sliderContainerRef, isAtStart, isAtEnd, handleScroll, handleCardClick } = useCarousel({
		initialCardWidth: 760,
	})

	if (!features.length) return null

	return (
		<section
			className={`feature-carousel-section ${theme === 'dark' ? 'bg-blue-dark text-white' : 'bg-white text-blue-dark'} @container`}
		>
			<div className="relative max-w-screen-2xl mx-auto pt-16 @5xl:pt-20 pb-6 @5xl:pb-10 overflow-hidden">
				<div className="px-3 sm:px-10">
					{tag ? (
						<Eyebrow variant={theme === 'dark' ? 'secondary' : 'primary'}>{tag}</Eyebrow>
					) : null}
					{title && !isStructuredTextEmpty(title.text) && (
						<h2 className="heading-size-1  max-w-xl mt-4 md:mt-10">
							<TextWithHighlight text={title} />
						</h2>
					)}
					{cta && <CtaDefault cta={cta} className="mt-6 lg:mt-8" />}

					<div className="flex flex-col sm:flex-row-reverse gap-6 items-end justify-between mb-12 @5xl:mb-14 mt-12 @5xl:mt-20 @7xl:mt-44">
						{description && !isStructuredTextEmpty(description) && (
							<div className="text-large max-w-lg 2xl:max-w-xl mr-auto sm:mr-0 pr-3 sm:pr-10">
								<StructuredText data={description} />
							</div>
						)}

						{/* Carousel Buttons */}
						<div className="flex gap-2 mr-auto">
							<IconButton
								icon="Arrow"
								className="rotate-180"
								variant={theme === 'dark' ? 'outline-soft-white' : 'outline-accent'}
								aria-label="scroll to previous card"
								onClick={() => handleScroll(-1)}
								disabled={isAtStart}
							/>
							<IconButton
								icon="Arrow"
								variant={theme === 'dark' ? 'outline-soft-white' : 'outline-accent'}
								aria-label="scroll to next card"
								onClick={() => handleScroll(1)}
								disabled={isAtEnd}
							/>
						</div>
					</div>
				</div>

				{/* Carousel Container */}
				<div
					className="flex flex-row gap-2.5 md:gap-5 overflow-x-auto scroll-hidden 2xl:rounded-xl pb-3 px-3 sm:px-10"
					ref={sliderContainerRef}
				>
					{features.map((feature, idx) => (
						<div
							key={feature.id as string}
							onClick={() => handleCardClick(idx)}
							className="relative min-w-[342px] md:min-w-[760px] h-[460px] md:h-[550px] text-white rounded-xl max-md:border-b-8 border-blue overflow-hidden cursor-pointer
							before:content-[''] before:absolute before:inset-0 before:bg-black/10
							after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-t after:from-black/40 after:to-transparent"
						>
							<span className="font-mono text-white text-sm absolute top-6 left-4 md:top-8 md:left-10">
								{idx < 9 && '0'}
								{idx + 1}.
							</span>

							{(!isStructuredTextEmpty(feature.title?.text) ||
								!isStructuredTextEmpty(feature.description)) && (
								<div className="absolute flex flex-col gap-3 bottom-10 inset-x-10 z-10">
									{feature.title && !isStructuredTextEmpty(feature.title.text) && (
										<div className="relative text-3xl pl-3 before:content-[''] before:absolute before:w-1 before:-left-0 before:inset-y-1 before:bg-blue">
											<TextWithHighlight text={feature.title} />
										</div>
									)}

									{feature.description && !isStructuredTextEmpty(feature.description) && (
										<div className="text-lg ">
											<StructuredText data={feature.description} />
										</div>
									)}
								</div>
							)}

							{feature.image && (
								<Image
									src={feature.image?.[0]?.url}
									alt={feature.image?.[0]?.alt ?? 'Feature Image'}
									width={feature.image?.[0]?.width}
									height={feature.image?.[0]?.height}
									className="object-cover w-full h-full"
									sizes="(max-width: 768px) 342px, 760px"
								/>
							)}
						</div>
					))}
				</div>

				<div className="grid grid-cols-2 gap-5 md:gap-8 w-fit absolute top-0 right-0 translate-x-10 md:translate-x-16 -translate-y-20 md:-translate-y-32">
					{Array.from({ length: 4 }).map((_, index) => (
						<CircleGraphic key={`shape-${index + 1}`} theme={theme ?? 'light'} />
					))}
				</div>
			</div>
		</section>
	)
}
